import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Brain, Trash2, Link2, MessageCircleQuestion, FileText, GitCompareArrows } from "lucide-react";

export const MemoryPanel = ({ open, items, onClose, onSelect, onClear }) => (
  <AnimatePresence>
    {open && (
      <>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        />
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          data-testid="memory-panel"
          className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md nexx-glass border-l border-border flex flex-col"
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-border">
            <div className="flex items-center gap-2">
              <Brain strokeWidth={1.5} className="w-4 h-4 text-primary" />
              <span className="font-heading text-lg">Semantic Memory</span>
            </div>
            <button data-testid="close-memory-btn" onClick={onClose} className="p-2 rounded-full hover:bg-secondary transition-colors">
              <X strokeWidth={1.5} className="w-4 h-4" />
            </button>
          </div>

          <div className="flex-1 overflow-auto px-3 py-3 scrollbar-thin">
            {items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center mt-16 px-6">
                Everything you interpret or search is remembered here — privately, in this browser only.
              </p>
            ) : (
              items.map((item) => (
                <button
                  key={item.id}
                  data-testid="memory-item"
                  onClick={() => onSelect(item)}
                  className="w-full text-left px-3 py-4 rounded-lg hover:bg-secondary transition-colors border-b border-border/60"
                >
                  <div className="flex items-center gap-2 mb-1.5">
                    {item.type === "crossref" ? (
                      <GitCompareArrows strokeWidth={1.5} className="w-3.5 h-3.5 text-primary" />
                    ) : item.type === "document" ? (
                      <FileText strokeWidth={1.5} className="w-3.5 h-3.5 text-primary" />
                    ) : item.type === "interpret" ? (
                      <Link2 strokeWidth={1.5} className="w-3.5 h-3.5 text-primary" />
                    ) : (
                      <MessageCircleQuestion strokeWidth={1.5} className="w-3.5 h-3.5 text-primary" />
                    )}
                    <span className="text-[10px] tracking-[0.16em] uppercase text-muted-foreground">
                      {item.type === "crossref" ? "Cross-Ref" : item.type === "document" ? "Document" : item.type === "interpret" ? "Interpret" : "NexxSearch"}
                    </span>
                  </div>
                  <p className="font-heading text-base leading-tight text-foreground line-clamp-2">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{item.subtitle}</p>
                </button>
              ))
            )}
          </div>

          {items.length > 0 && (
            <div className="px-6 py-4 border-t border-border">
              <button
                data-testid="clear-memory-btn"
                onClick={onClear}
                className="flex items-center gap-2 text-xs tracking-[0.14em] uppercase text-muted-foreground hover:text-destructive transition-colors"
              >
                <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" /> Clear memory
              </button>
            </div>
          )}
        </motion.aside>
      </>
    )}
  </AnimatePresence>
);
